import { useState } from 'react'
import { Trans } from '@lingui/react/macro'

import {
  useFileDownloadLinkContent,
  useFileDownloadLinkContentQueryKey,
} from '@jotta/file-utils'
import { useFileUpload } from '@jotta/grpc-connect-client/useFileUpload'
import { AllocateConflictHandler } from '@jotta/grpc-connect-openapi/file'
import { ItemAction } from '@jotta/grpc-web/no/jotta/openapi/file/file.v2_pb'
import { queryClient } from '@jotta/query'
import type { PathItemObject } from '@jotta/types/Files'
import { Button } from '@jotta/ui/Button'
import SvgCheck from '@jotta/ui/SvgCheck'
import SvgEdit from '@jotta/ui/SvgEdit'
import SvgX from '@jotta/ui/SvgX'

export function FileTextView({
  file,
  token: tokenParam,
}: {
  file: PathItemObject
  token?: string
}) {
  const token = tokenParam || file.accessTokenOverride
  const { data } = useFileDownloadLinkContent({
    variables: {
      path: file.path,
      includeDeleted: Boolean(file.deletedAtMillis),
      token,
    },
  })

  const canEdit = file.actionList.includes(ItemAction.UPDATE)
  const [edit, setEdit] = useState(false)
  const [editedText, setEditedText] = useState('')

  const { mutate, isPending } = useFileUpload({
    token: token,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: useFileDownloadLinkContentQueryKey,
      })
      setEdit(false)
    },
  })

  return (
    <div className="flex size-full flex-col gap-4 bg-primary p-4 text-primary">
      {edit ? (
        <textarea
          className="textarea size-full"
          disabled={isPending}
          value={editedText}
          onChange={e => setEditedText(e.target.value)}
        />
      ) : (
        <pre className="size-full overflow-y-auto">{data}</pre>
      )}
      {canEdit && (
        <div className="flex gap-2 self-end">
          {edit ? (
            <>
              <Button
                className="btn"
                disabled={isPending}
                icon={SvgX}
                onClick={() => {
                  setEditedText(data || '')
                  setEdit(false)
                }}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button
                className="btn btn-primary"
                loading={isPending}
                icon={SvgCheck}
                onClick={() => {
                  const upload = new File([editedText], file.name)
                  mutate({
                    path: file.path,
                    file: upload,
                    conflictHandler:
                      AllocateConflictHandler.CREATE_NEW_REVISION,
                  })
                }}
              >
                <Trans>Save</Trans>
              </Button>
            </>
          ) : (
            <Button
              className="btn"
              icon={SvgEdit}
              onClick={() => {
                setEditedText(data || '')
                setEdit(true)
              }}
            >
              <Trans>Edit</Trans>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
